import React from "react";
import {BookAppointmentRequest, BookClassRequest, CreateCheckoutSessionRequest, space} from "../../compiled";
import {PurchaseOption, SelectedBooking} from "./BookingFlow";
import {useBookClassRpc} from "./RpcHooks/BookClassHook";
import {useBookAppointmentRpc} from "./RpcHooks/BookAppointmentHook";
import {useCreateCheckoutSessionRpc} from "../../util/RpcHooks/CreateCheckoutSessionRpc";
import BookingPurchaseOption = space.kenko.proto.BookingPurchaseOption;
import {ActionButton} from "../../util/UI/ActionButton";

type Props = {
  businessId: string,
  selectedBooking: SelectedBooking,
  purchaseOption: PurchaseOption,
  isPreview: boolean, // if they're just testing the booking flow booking is disabled.
}

/**
 * The final "confirm your purchase" page. This shows the provider's terms and conditions
 * and redirects either to Stripe (if payment required) or to book the appointment on Kensu
 * (when no payment is required).
 *
 * This works for appointments, classes, and hopefully all others.
 */
export const ConfirmBooking = (props: Props) => {

  const createCheckoutSessionRpc = useCreateCheckoutSessionRpc();
  const bookClassRpc = useBookClassRpc();
  const bookAppointmentRpc = useBookAppointmentRpc();


  /**
   * When buying a single class pack or a new class package the user goes to
   * stripe to handle payment, then is redirected to the confirmation page.
   */
  const handleReservation = () => {
    const appt = props.selectedBooking.appointment;
    bookAppointmentRpc.send(new BookAppointmentRequest({
      businessId: props.businessId,
      practitionerId: appt?.practitioner.practitionerId,
      locationId: appt?.location?.locationId,
      virtualAppt: appt?.virtualAppt,
      serviceId: appt?.service.sku,
      appointmentTime: appt?.timestamp,
      purchaseOption: {
        postpaidReservationOnly: appt?.service.paymentPolicy?.postpaid?.price
      }
    }));
  }

  /**
   * When buying a single class pack or a new class package the user goes to
   * stripe to handle payment, then is redirected to the confirmation page.
   */
  const handleStripePayment = () => {

    createCheckoutSessionRpc.send(new CreateCheckoutSessionRequest({
          businessId: props.businessId,
          bookingType: {
            appointmentBooking: props.selectedBooking.appointment ? {
              serviceSku: props.selectedBooking.appointment.service.sku,
              locationId: props.selectedBooking.appointment.location?.locationId,
              isVirtual: props.selectedBooking.appointment.virtualAppt,
              timestamp: props.selectedBooking.appointment.timestamp,
              practitionerId: props.selectedBooking.appointment.practitioner.practitionerId,
            } : undefined,
            classBooking: props.selectedBooking.class ? {
              eventInstanceId: props.selectedBooking.class?.eventInstanceId,
            } : undefined,
          },
          paymentLineItem: {
            // only one of these should actually be set
            singleClassPriceCategory: props.purchaseOption.buySingleCredit?.priceCategory?.categoryId,
            classCredit: props.purchaseOption.buySingleCredit?.class?.classTemplateId,
            serviceCredit: props.purchaseOption.buySingleCredit?.service?.sku,
            packageId: props.purchaseOption.buyNewClassPackage?.id,
          }
        }
    ));
  };

  /**
   * The user already has a credit or package, so we can just book the class.
   */
  const handleNonStripeBooking = () => {
    
    let purchaseOption: BookingPurchaseOption;
    if (props.purchaseOption.useExistingClassPackage) {
      purchaseOption = new BookingPurchaseOption({
        prepaidPackageId: props.purchaseOption.useExistingClassPackage.purchasedClassPackId,
      });
    } else if (props.purchaseOption.useExistingClassCredit) {
      purchaseOption = new BookingPurchaseOption({
        prepaidSingleCreditId: props.purchaseOption.useExistingClassCredit.classCreditId,
      })
    } else {
      throw new Error("non supported purchase option");
    }

    if (props.selectedBooking.class) {
      bookClassRpc.send(new BookClassRequest({
        businessId: props.businessId,
        eventInstanceId: props.selectedBooking.class?.eventInstanceId,
        purchaseOption: purchaseOption,
      }));
    } else if (props.selectedBooking.appointment) {
      bookAppointmentRpc.send(new BookAppointmentRequest({
        businessId: props.businessId,
        practitionerId: props.selectedBooking.appointment?.practitioner.practitionerId,
        locationId: props.selectedBooking.appointment?.location?.locationId,
        virtualAppt: props.selectedBooking.appointment?.virtualAppt,
        serviceId: props.selectedBooking.appointment?.service.sku,
        appointmentTime: props.selectedBooking.appointment?.timestamp,
        purchaseOption: purchaseOption,
      }))
      
    }
  }

  // Currently only services can be postpaid.
  // TODO - if classes can be postpaid add it here.
  const isPostpaid = props.selectedBooking.appointment?.service?.paymentPolicy?.postpaid;

  return <div style={{display: 'flex', flexDirection: "column", textAlign: 'start', gap: 10}}>
    <h3>Confirm booking</h3>

    {isPostpaid && <>
      <div>Click to confirm your reservation. Payment is required immediately following after the appointment.</div>
      <div>
        <ActionButton onClick={handleReservation} rpcState={bookAppointmentRpc.rpcState}>Reserve</ActionButton>
      </div>
    </>}

    {(props.purchaseOption.buySingleCredit || props.purchaseOption.buyNewClassPackage) && <>
      <div>You'll be redirect to Stripe to make your purchase.</div>
      <ActionButton onClick={handleStripePayment} rpcState={createCheckoutSessionRpc.rpcState}>
        Continue to Payment
      </ActionButton>
    </>}

    {(props.purchaseOption.useExistingClassCredit || props.purchaseOption.useExistingClassPackage) && <>
      <div>Confirm your booking using your
        existing {props.purchaseOption.useExistingClassCredit ? 'credit' : 'package'}</div>
      <ActionButton onClick={handleNonStripeBooking}
      rpcState={props.selectedBooking.class ? bookClassRpc.rpcState : bookAppointmentRpc.rpcState}>
        Confirm
      </ActionButton>
    </>
    }

    {/*<div style={{fontStyle: 'italic'}}>*/}
    {/*  By confirming, I am agreeing to [Provider's] Terms and Conditions and Cancellation Policy.*/}
    {/*</div>*/}

    {createCheckoutSessionRpc.rpcState.state == 'error' && <div className={'ErrorText'}>{createCheckoutSessionRpc.rpcState.message}</div>}
    {bookClassRpc.rpcState.state == 'error' && <div className={'ErrorText'}>{bookClassRpc.rpcState.message}</div>}
    {bookAppointmentRpc.rpcState.state == 'error' && <div className={'ErrorText'}>{bookAppointmentRpc.rpcState.message}</div>}

  </div>;

}